// v2
// Path: frontend/src/components/SyncHub/shared/PresetAccordionItem.jsx
// Inputs: preset, isExpanded, onToggleExpand, token, sourceSystem, targetSystem, availableTables, targetEntities, assignedEntitiesInSet, onSavePreset, onDuplicatePreset, onRenamePreset, onDeletePreset, onResetPresetDefaults, onSavingStateChange
// Outputs: Tier 2 5-column Tree Grid Pipeline row (250px 1fr 80px 1fr 100px) with stacking sticky header, CSS grid slide animation, auto-scroll, dynamic entity filtering, auto-spawning required fields, and optimistic debounced auto-saving.
// Summary: High-performance pipeline accordion item managing self-contained ETL field mappings, persistent ghost rows, locked required destination fields, and debounced background persistence.

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import SearchableSelect from '../SearchableSelect';
import MappingDesignerTable from './MappingDesignerTable';
import { getQbEntityMeta, normalizeEntityName } from '../entityCatalogs';

// Auto-injected unbox helper for standardized API responses
const unbox = async (res) => {
    const payload = await res.json();
    if (payload && typeof payload === 'object' && payload.success === true && payload.data !== undefined) {
        return payload.data;
    }
    return payload;
};

// Helper to merge target fields with existing preset mappings
// Condensed rule: DO NOT pre-render all unmapped fields from target schema!
// ONLY retain: 1. Auto-spawned required fields, 2. Actively mapped rows, 3. User-created placeholder rows.
const mergeTargetFieldsWithMappingsCondensed = (tFields, rawMappings) => {
    const existingMap = new Map();
    (rawMappings || []).forEach(m => {
        if (m && m.target) {
            existingMap.set(m.target.toLowerCase(), {
                target: m.target,
                label: m.label || m.target,
                sources: Array.isArray(m.sources) && m.sources.length > 0
                    ? m.sources
                    : (m.source ? (m.source.includes('+') ? m.source.split('+').map(s => s.trim()) : [m.source]) : []),
                source: m.source || (Array.isArray(m.sources) ? m.sources.join(m.delimiter !== undefined ? m.delimiter : ' - ') : ''),
                delimiter: m.delimiter !== undefined ? m.delimiter : ' - ',
                template: m.template || '',
                isCustom: !!m.isCustom
            });
        }
    });

    // Auto-spawn required destination fields from target schema if missing
    (tFields || []).forEach(tf => {
        const isRequired = !!tf.required || (typeof tf.rules === 'string' && tf.rules.toUpperCase().includes('REQUIRED'));
        const lower = tf.name.toLowerCase();
        if (isRequired && !existingMap.has(lower)) {
            existingMap.set(lower, {
                target: tf.name,
                label: tf.label || tf.name,
                sources: [],
                source: '',
                delimiter: ' - ',
                template: '',
                isRequired: true
            });
        }
    });

    return Array.from(existingMap.values());
};

export default function PresetAccordionItem({
    preset,
    isExpanded = false,
    onToggleExpand = () => {},
    token,
    sourceSystem = 'Passport',
    targetSystem = 'QuickBooks',
    availableTables = [],
    targetEntities = [],
    assignedEntitiesInSet = new Set(),
    onSavePreset = async () => {},
    onDuplicatePreset = () => {},
    onRenamePreset = () => {},
    onDeletePreset = () => {},
    onResetPresetDefaults = () => {},
    onSavingStateChange = () => {}
}) {
    const containerRef = useRef(null);

    // Current entity and table for this preset pipeline
    const canonicalEntity = normalizeEntityName(preset.TargetQBEntity || preset.EntityType || 'Customer');
    const entityMeta = getQbEntityMeta(canonicalEntity);
    const defaultTable = preset.SourceTable || entityMeta?.defaultTable || '';

    const [selectedTable, setSelectedTable] = useState(defaultTable);
    const [selectedEntity, setSelectedEntity] = useState(canonicalEntity);
    const [definitions, setDefinitions] = useState(null);
    const [mappings, setMappings] = useState([]);
    const [loadingDefinitions, setLoadingDefinitions] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [saving, setSaving] = useState(false);

    // Debounced Auto-Save Refs
    const saveTimerRef = useRef(null);
    const latestStateRef = useRef({ mappings, selectedTable, selectedEntity });

    useEffect(() => {
        latestStateRef.current = { mappings, selectedTable, selectedEntity };
    }, [mappings, selectedTable, selectedEntity]);

    // Sync state if preset updates from parent
    useEffect(() => {
        setSelectedTable(preset.SourceTable || entityMeta?.defaultTable || '');
        setSelectedEntity(canonicalEntity);
    }, [preset.SourceTable, canonicalEntity, entityMeta?.defaultTable]);

    // Auto-Scroll to Top on Expand (delayed 100ms to allow layout calculation & smooth transition)
    useEffect(() => {
        if (isExpanded && containerRef.current) {
            const timer = setTimeout(() => {
                if (containerRef.current) {
                    containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [isExpanded]);

    // Fetch field definitions when expanded
    const fetchDefinitions = useCallback(async () => {
        if (!isExpanded) return;
        setLoadingDefinitions(true);
        try {
            const activeTbl = selectedTable || preset.SourceTable || '';
            const params = new URLSearchParams({
                entityType: selectedEntity,
                sourceDatabaseName: 'pbsdata00',
                tableName: activeTbl
            });

            const res = await fetch(`/api/sync/field-definitions/${encodeURIComponent(selectedEntity)}?${params.toString()}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (res.ok) {
                const data = await unbox(res);
                setDefinitions(data);

                let initialRaw = Array.isArray(preset.mappings) ? preset.mappings : [];
                if ((!initialRaw || initialRaw.length === 0) && preset.FieldMappingsJson) {
                    try { initialRaw = JSON.parse(preset.FieldMappingsJson); } catch(e) {}
                }

                const targetFields = data.targetFields || [];
                const merged = mergeTargetFieldsWithMappingsCondensed(targetFields, initialRaw);
                setMappings(merged);
                setIsDirty(false);
            }
        } catch (err) {
            console.error('[PresetAccordionItem] Error loading definitions:', err);
        } finally {
            setLoadingDefinitions(false);
        }
    }, [isExpanded, selectedTable, selectedEntity, preset.SourceTable, preset.mappings, preset.FieldMappingsJson, token]);

    useEffect(() => {
        if (isExpanded) {
            fetchDefinitions();
        }
    }, [isExpanded, fetchDefinitions]);

    // Optimistic Save Routine
    const executeSave = useCallback(async (stateToSave) => {
        const { mappings: curMappings, selectedTable: curTable, selectedEntity: curEntity } = stateToSave;
        setSaving(true);
        onSavingStateChange(true);
        try {
            const cleanMappings = curMappings
                .filter(m => m.target)
                .map(m => ({
                    target: m.target.trim(),
                    label: m.label ? m.label.trim() : undefined,
                    source: m.source ? m.source.trim() : (m.sources ? m.sources.join(m.delimiter || ' - ') : ''),
                    sources: Array.isArray(m.sources) && m.sources.length > 0 ? m.sources : (m.source ? [m.source.trim()] : []),
                    delimiter: m.delimiter !== undefined ? m.delimiter : ' - ',
                    template: m.template || undefined
                }));

            await onSavePreset({
                mappingId: preset.MappingID,
                setName: preset.SetName,
                sourceSystem: preset.SourceSystem || sourceSystem,
                targetSystem: preset.TargetSystem || targetSystem,
                entityType: curEntity,
                targetQBEntity: curEntity,
                sourceTable: curTable,
                mappingName: preset.MappingName,
                mappings: cleanMappings
            });

            setIsDirty(false);
        } catch (err) {
            console.error('[PresetAccordionItem] Auto-save error:', err);
        } finally {
            setSaving(false);
            onSavingStateChange(false);
        }
    }, [preset, sourceSystem, targetSystem, onSavePreset, onSavingStateChange]);

    // Trigger debounced save
    const triggerDebouncedSave = useCallback(() => {
        setIsDirty(true);
        if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current);
        }
        saveTimerRef.current = setTimeout(() => {
            executeSave(latestStateRef.current);
        }, 1500);
    }, [executeSave]);

    // Flush pending save immediately on collapse
    useEffect(() => {
        if (!isExpanded && isDirty) {
            if (saveTimerRef.current) {
                clearTimeout(saveTimerRef.current);
            }
            executeSave(latestStateRef.current);
        }
    }, [isExpanded, isDirty, executeSave]);

    // Compute live mapping coverage count
    const mappedFieldsCount = useMemo(() => {
        if (mappings.length > 0) {
            return mappings.filter(m => {
                const s = Array.isArray(m.sources) ? m.sources : (m.source ? [m.source] : []);
                return s.length > 0 && s.some(x => x && x.trim().length > 0);
            }).length;
        }
        const raw = Array.isArray(preset.mappings) ? preset.mappings : [];
        return raw.filter(m => {
            const s = Array.isArray(m.sources) ? m.sources : (m.source ? [m.source] : []);
            return s.length > 0 && s.some(x => x && x.trim().length > 0);
        }).length;
    }, [mappings, preset.mappings]);

    // Mutate field mapping row
    const handleCustomMappingChange = (targetName, field, val) => {
        setMappings(prev => prev.map(m => {
            if (m.target.toLowerCase() === targetName.toLowerCase()) {
                const updated = { ...m, [field]: val };
                if (field === 'sources') {
                    const delim = m.delimiter !== undefined ? m.delimiter : ' - ';
                    updated.source = Array.isArray(val) ? val.join(delim) : String(val);
                }
                return updated;
            }
            return m;
        }));
        triggerDebouncedSave();
    };

    // Remove mapping row
    const handleRemoveCustomRow = (targetName) => {
        setMappings(prev => prev.filter(m => m.target.toLowerCase() !== targetName.toLowerCase()));
        triggerDebouncedSave();
    };

    // Reset mapping row (clear assigned sources)
    const handleResetMappingRow = (targetName) => {
        setMappings(prev => prev.map(m => {
            if (m.target.toLowerCase() === targetName.toLowerCase()) {
                return { ...m, sources: [], source: '' };
            }
            return m;
        }));
        triggerDebouncedSave();
    };

    // Add new mapping from Tier 3 Ghost Row
    const handleAddMappingFromGhost = (newMapping) => {
        if (!newMapping.target) return;
        setMappings(prev => {
            const exists = prev.some(m => m.target.toLowerCase() === newMapping.target.toLowerCase());
            if (exists) return prev;
            return [...prev, newMapping];
        });
        triggerDebouncedSave();
    };

    // Prepare table dropdown options
    const tableOptions = useMemo(() => {
        return availableTables.map(t => {
            const tName = typeof t === 'string' ? t : (t.name || t.TableName || '');
            const fCount = t.fieldCount || t.FieldCount || (t.columns ? t.columns.length : 0);
            const countStr = fCount > 0 ? ` (${fCount} cols)` : '';
            return {
                value: tName,
                label: `${tName}${countStr}`,
                description: t.description || t.Description || undefined,
                isPowered: tName.toUpperCase() === (selectedTable || '').toUpperCase() && mappedFieldsCount > 0
            };
        });
    }, [availableTables, selectedTable, mappedFieldsCount]);

    // Prepare entity dropdown options strictly excluding other pipelines' entities in this Set
    const entityOptions = useMemo(() => {
        const curLower = selectedEntity.toLowerCase();
        return targetEntities
            .filter(ent => {
                const eName = typeof ent === 'string' ? ent : (ent.entity || ent.name);
                return eName.toLowerCase() === curLower || !assignedEntitiesInSet.has(eName.toLowerCase());
            })
            .map(ent => {
                const eName = typeof ent === 'string' ? ent : (ent.entity || ent.name);
                const eLabel = typeof ent === 'string' ? ent : (ent.label || ent.name || ent.entity);
                const isCurrent = eName.toLowerCase() === curLower;
                return {
                    value: eName,
                    label: eLabel,
                    isPowered: isCurrent ? mappedFieldsCount > 0 : false
                };
            });
    }, [targetEntities, selectedEntity, assignedEntitiesInSet, mappedFieldsCount]);

    return (
        <div
            ref={containerRef}
            className={`transition-colors duration-200 border-b border-slate-700/50 ${
                isExpanded
                    ? 'bg-slate-800/40'
                    : 'bg-slate-900/60 hover:bg-slate-850/60'
            }`}
        >
            {/* ══════════════════════════════════════════════════════════════════ */}
            {/* TIER 2: 5-COLUMN TREE GRID PIPELINE HEADER ROW                     */}
            {/* Ultra-condensed h-9, px-2 py-0.5, sticky top-9 (36px)              */}
            {/* ══════════════════════════════════════════════════════════════════ */}
            <div
                onClick={onToggleExpand}
                className={`grid grid-cols-[250px_minmax(0,1fr)_80px_minmax(0,1fr)_100px] gap-2 items-center h-9 max-h-9 px-2 py-0.5 cursor-pointer select-none transition-colors border-b border-slate-700/50 ${
                    isExpanded
                        ? 'sticky top-9 z-40 bg-slate-850/95 backdrop-blur-sm shadow-sm'
                        : 'bg-slate-850/80 hover:bg-slate-800'
                }`}
            >
                {/* ── COL 1 (250px): EXPAND ICON + PIPELINE TITLE ── */}
                <div className="flex items-center gap-1.5 min-w-0">
                    <span className={`text-slate-400 text-xs transition-transform duration-200 ${isExpanded ? 'rotate-90 text-indigo-400' : ''}`}>
                        ▶
                    </span>
                    <span className="material-symbols-outlined text-indigo-400 text-[16px] shrink-0">account_tree</span>
                    <span className="font-bold text-xs text-white tracking-wide truncate" title={preset.MappingName}>
                        {preset.MappingName || `${selectedEntity} Pipeline`}
                    </span>
                </div>

                {/* ── COL 2 (1fr): ERP SOURCE TABLE (SEARCHABLE SELECT) ── */}
                <div onClick={e => e.stopPropagation()} className="min-w-0">
                    <SearchableSelect
                        options={tableOptions}
                        value={selectedTable}
                        onChange={tbl => {
                            const upper = (tbl || '').toUpperCase();
                            setSelectedTable(upper);
                            triggerDebouncedSave();
                        }}
                        placeholder="Select source table..."
                        allowCustom={true}
                        className="w-full text-xs"
                    />
                </div>

                {/* ── COL 3 (80px, center): DIRECTIONAL FLOW ARROW ── */}
                <div className="flex items-center justify-center gap-1 text-center select-none">
                    <span className="text-slate-500 font-bold text-xs">➔</span>
                    <span className="px-1.5 py-0.2 rounded-full bg-slate-900 border border-slate-750 text-indigo-300 font-mono text-[10px]">
                        {mappedFieldsCount} Mapped
                    </span>
                </div>

                {/* ── COL 4 (1fr): TARGET DESTINATION ENTITY (FILTERED) ── */}
                <div onClick={e => e.stopPropagation()} className="min-w-0">
                    <SearchableSelect
                        options={entityOptions}
                        value={selectedEntity}
                        onChange={ent => {
                            const canonical = normalizeEntityName(ent);
                            setSelectedEntity(canonical);
                            triggerDebouncedSave();
                        }}
                        placeholder="Select target entity..."
                        className="w-full text-xs"
                    />
                </div>

                {/* ── COL 5 (100px, right): [RESET] & [DELETE] ACTIONS ── */}
                <div className="flex items-center justify-end gap-1 shrink-0" onClick={e => e.stopPropagation()}>
                    <button
                        type="button"
                        onClick={() => onResetPresetDefaults(preset)}
                        className="px-1.5 py-0.5 bg-slate-800 hover:bg-slate-750 text-slate-300 hover:text-white rounded border border-slate-700 text-[10px] font-semibold transition-colors"
                        title="Reset pipeline mappings to defaults"
                    >
                        Reset
                    </button>

                    <button
                        type="button"
                        onClick={() => onDeletePreset(preset)}
                        className="px-1.5 py-0.5 bg-slate-800 hover:bg-red-950/60 text-slate-400 hover:text-red-300 rounded border border-slate-700 hover:border-red-800/60 text-[10px] font-semibold transition-colors"
                        title="Delete this pipeline"
                    >
                        Delete
                    </button>
                </div>
            </div>

            {/* ══════════════════════════════════════════════════════════════════ */}
            {/* TIER 3: CSS GRID SLIDE ANIMATION CONTAINER (500ms ease-in-out)     */}
            {/* ══════════════════════════════════════════════════════════════════ */}
            <div className={`grid transition-[grid-template-rows] duration-500 ease-in-out ${isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
                <div className="overflow-hidden min-h-0">
                    {loadingDefinitions ? (
                        <div className="py-4 text-center text-slate-400 text-xs flex items-center justify-center gap-2">
                            <span className="material-symbols-outlined text-indigo-400 animate-spin text-sm">sync</span>
                            Loading field schema definitions...
                        </div>
                    ) : (
                        <div className="p-0">
                            <MappingDesignerTable
                                isStandardMode={true}
                                selectedQBEntity={selectedEntity}
                                activeTableName={selectedTable}
                                mappings={mappings}
                                definitions={definitions}
                                sourceColumns={definitions?.sourceFields || definitions?.sourceColumns || []}
                                onAddMapping={handleAddMappingFromGhost}
                                handleCustomMappingChange={handleCustomMappingChange}
                                handleRemoveCustomRow={handleRemoveCustomRow}
                                handleResetMappingRow={handleResetMappingRow}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
